import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileDown, RefreshCw, Mail, MailX, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useWS } from '../WSContext';

// Günlük performans raporu arşivi — Performans sayfasının "Günlük Raporlar" sekmesi.
// Rapor sunucuda her gece 00:00 (İstanbul) üretilir: perf_report.py. Bu bileşen yalnız
// arşivi gösterir, PDF indirir, elle transfer girer. Sayılar sunucudan hazır gelir —
// burada hesap YAPILMAZ ki ekran ile PDF/mail asla ayrışmasın.

interface Props { t: any; card: string; }

const fmt = (v: number | null | undefined, d = 2, sign = false) => {
  if (v == null) return '—';
  if (Math.abs(v) < 0.5 * 10 ** -d) v = 0;       // "−0,00" yazmasın
  const s = v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
  return sign && v > 0 ? '+' + s : s;
};
const tone = (v: number | null | undefined, t: any) =>
  v == null || Math.abs(v) < 0.005 ? t.text : v > 0 ? t.positive : t.negative;
const dayLabel = (day: string) =>
  new Date(day + 'T12:00:00').toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
const RESIDUAL_WARN = 1;     // $ — mutabakat farkı bundan büyükse uyar

export default function PerfReports({ t, card }: Props) {
  const { connected, sendWs, subscribe } = useWS() as any;
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ account: '', ts: '', amount: '', currency: 'USD', note: '' });
  const [ledgerOpen, setLedgerOpen] = useState(false);

  useEffect(() => {
    if (connected) sendWs({ type: 'get_perf_reports' });
  }, [connected, sendWs]);

  useEffect(() => subscribe((m: any) => {
    if (m.type === 'perf_reports') {
      setData(m.payload); setBusy(null); setErr(m.payload?.error || null);
      setOpen(o => o ?? m.payload?.reports?.[0]?.day ?? null);
    }
    if (m.type === 'perf_report_pdf') {
      setBusy(null);
      const { day, b64 } = m.payload || {};
      if (!b64) { setErr(`${day} PDF'i bulunamadı`); return; }
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `performans-${day}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  }), [subscribe]);

  const reports: any[] = data?.reports || [];
  const accounts: any[] = data?.accounts || [];
  const nameOf = useMemo(() => Object.fromEntries(accounts.map(a => [a.id, a.name])), [accounts]);

  // İlk tam gün: görüntü gün başından (±45 dk) önce başlamış olmalı
  const firstFull = useMemo(() => {
    if (!data?.firstSnapshot) return null;
    const d = new Date(data.firstSnapshot + 3 * 3600e3);            // İstanbul
    const mid = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - 3 * 3600e3;
    const day = data.firstSnapshot <= mid + 45 * 60e3 ? mid : mid + 86400e3;
    return new Date(day + 3 * 3600e3).toISOString().slice(0, 10);
  }, [data]);

  const submit = () => {
    const amount = parseFloat(form.amount.replace(',', '.'));
    const ts = form.ts ? new Date(form.ts).getTime() : NaN;
    if (!form.account || !isFinite(amount) || !isFinite(ts)) { setErr('Hesap, tarih ve tutar gerekli'); return; }
    setBusy('add');
    sendWs({ type: 'add_manual_transfer', payload: {
      account: form.account, ts, amount, currency: form.account === 'binance1-cm' ? (form.currency || 'ETH') : 'USD',
      note: form.note } });
    setForm(f => ({ ...f, amount: '', note: '' }));
  };

  const inp = `${t.inp} border rounded-lg px-2.5 py-1.5 text-xs outline-none`;

  if (!data) {
    return <div className={`${card} p-8 text-center ${t.muted} text-sm`}>
      {connected ? 'Raporlar yükleniyor…' : 'Backend bağlantısı bekleniyor...'}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Durum şeridi */}
      <div className={`${card} px-4 py-3 flex items-center gap-4 flex-wrap text-xs`}>
        <span className={`flex items-center gap-1.5 ${data.mailConfigured ? t.positive : t.muted}`}>
          {data.mailConfigured ? <Mail size={13} /> : <MailX size={13} />}
          {data.mailConfigured ? 'Mail gönderimi açık' : 'Mail kapalı — bots/report/.env ayarlanmadı (rapor yine arşivlenir)'}
        </span>
        <span className={t.muted}>Rapor her gece 00:00 (İstanbul) üretilir</span>
        {firstFull && <span className={t.muted}>İlk tam gün: {dayLabel(firstFull)}</span>}
      </div>

      {err && <div className="px-4 py-2 rounded-lg text-xs bg-rose-500/10 border border-rose-500/30 text-rose-300">{err}</div>}

      {reports.length === 0 && (
        <div className={`${card} p-8 text-center text-sm ${t.muted}`}>
          Henüz rapor yok. Equity anlık görüntüleri saatlik birikiyor
          {firstFull && <> — ilk rapor <b className={t.text}>{dayLabel(firstFull)}</b> günü bitince üretilecek</>}.
        </div>
      )}

      {reports.map(r => {
        const rep = r.report, tot = rep.total, isOpen = open === r.day;
        const warn = tot.residual != null && Math.abs(tot.residual) > RESIDUAL_WARN;
        return (
          <div key={r.day} className={`${card} overflow-hidden`}>
            <button onClick={() => setOpen(isOpen ? null : r.day)}
              className={`w-full px-4 py-3 flex items-center justify-between gap-3 flex-wrap hover:opacity-90 ${isOpen ? `border-b ${t.cardBorder}` : ''}`}>
              <div className="flex items-center gap-3">
                <span className={t.muted}>{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
                <span className={`text-sm font-semibold ${t.text}`}>{dayLabel(r.day)}</span>
                {warn && <span className="flex items-center gap-1 text-[11px] text-amber-400"><AlertTriangle size={12} /> fark {fmt(tot.residual, 2, true)} $</span>}
              </div>
              <div className="flex items-center gap-5 text-xs">
                <span className={t.muted}>Net PnL <b className={`font-mono ${tone(tot.net, t)}`}>{fmt(tot.net, 2, true)} $</b></span>
                <span className={t.muted}>Δ Equity <b className={`font-mono ${tone(tot.dEq, t)}`}>{fmt(tot.dEq, 2, true)} $</b></span>
                <span className={r.mailedAt ? t.positive : t.muted} title={r.mailError || ''}>
                  {r.mailedAt ? <Mail size={13} /> : <MailX size={13} />}</span>
              </div>
            </button>
            {isOpen && <ReportDetail rep={rep} t={t} />}
            {isOpen && (
              <div className={`px-4 py-3 border-t ${t.cardBorder} flex items-center gap-2 flex-wrap`}>
                <button disabled={!!busy} onClick={() => { setBusy('pdf'); sendWs({ type: 'get_perf_report_pdf', payload: { day: r.day } }); }}
                  className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${t.btnSecondary}`}>
                  <FileDown size={13} /> PDF indir</button>
                <button disabled={!!busy} onClick={() => { setBusy('regen'); sendWs({ type: 'regen_perf_report', payload: { day: r.day } }); }}
                  className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${t.btnSecondary}`}>
                  <RefreshCw size={13} className={busy === 'regen' ? 'animate-spin' : ''} /> Yeniden hesapla</button>
                <span className={`text-[11px] ${t.muted}`}>
                  Üretildi {new Date(r.generatedAt).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}
                  {r.mailError && <> · mail hatası: {r.mailError}</>}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {/* Elle transfer — API'nin görmediği para hareketi */}
      <div className={`${card} overflow-hidden`}>
        <button onClick={() => setLedgerOpen(v => !v)}
          className={`w-full px-4 py-3 flex items-center gap-3 hover:opacity-90 ${ledgerOpen ? `border-b ${t.cardBorder}` : ''}`}>
          <span className={t.muted}>{ledgerOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
          <span className={`text-sm font-semibold ${t.text}`}>Transferler</span>
          <span className={`text-xs ${t.muted}`}>son 60 gün · API'den gelenler + elle girilenler</span>
        </button>
        {ledgerOpen && (
          <div className="p-4 space-y-3">
            <p className={`text-[11px] ${t.muted}`}>
              Transferler borsa API'lerinden otomatik okunur. Bir raporda "fark" çıkarsa ve sebebi API'nin görmediği bir
              para hareketiyse buradan girin — o günün raporu yeniden hesaplanır (mail tekrar gitmez).
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <select className={inp} value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))}>
                <option value="">Hesap seç</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <input type="datetime-local" className={inp} value={form.ts} onChange={e => setForm(f => ({ ...f, ts: e.target.value }))} />
              <input className={`${inp} w-28 font-mono`} placeholder="± tutar" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
              {form.account === 'binance1-cm'
                ? <input className={`${inp} w-16`} placeholder="ETH" value={form.currency}
                    onChange={e => setForm(f => ({ ...f, currency: e.target.value.toUpperCase() }))} />
                : <span className={`text-xs ${t.muted}`}>USD</span>}
              <input className={`${inp} w-48`} placeholder="not (ops.)" value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              <button disabled={!!busy} onClick={submit} className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${t.btnPrimary}`}>
                <Plus size={13} /> Ekle</button>
            </div>
            <p className={`text-[10px] ${t.muted}`}>+ hesaba giren, − hesaptan çıkan.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className={`${t.tableHeader} border-b`}>
                  {['Tarih', 'Hesap', 'Tutar', 'Tür', 'Kaynak', ''].map(h =>
                    <th key={h} className={`px-3 py-2 text-left font-medium ${t.muted}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(data.ledger || []).map((l: any) => (
                    <tr key={l.account + l.id} className={`border-b ${t.tableRow}`}>
                      <td className={`px-3 py-2 ${t.text}`}>{new Date(l.ts).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className={`px-3 py-2 ${t.text}`}>{nameOf[l.account] || l.account}</td>
                      <td className={`px-3 py-2 font-mono ${tone(l.amount, t)}`}>{fmt(l.amount, l.currency === 'ETH' || l.currency === 'BTC' ? 8 : 2, true)} {l.currency}</td>
                      <td className={`px-3 py-2 ${t.muted}`}>{l.note}</td>
                      <td className={`px-3 py-2 ${t.muted}`}>{l.source === 'manual' ? 'elle' : 'API'}</td>
                      <td className="px-3 py-2 text-right">
                        {l.source === 'manual' && (
                          <button title="Sil" onClick={() => sendWs({ type: 'delete_manual_transfer', payload: { id: l.id, ts: l.ts } })}
                            className="text-rose-400 hover:text-rose-300"><Trash2 size={13} /></button>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportDetail({ rep, t }: { rep: any; t: any }) {
  const tot = rep.total;
  const th = `px-2.5 py-2 text-right font-medium ${t.muted} whitespace-nowrap`;
  const td = `px-2.5 py-2 text-right font-mono whitespace-nowrap`;
  const Num = ({ v, d = 2, s = true }: { v: any; d?: number; s?: boolean }) =>
    <td className={`${td} ${s ? tone(v, t) : t.text}`}>{fmt(v, d, s)}</td>;
  return (
    <div className="p-4 space-y-5">
      {/* Özet */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[['Gün başı equity', tot.eq0, false], ['Gün sonu equity', tot.eq1, false], ['Δ Equity', tot.dEq, true],
          ['Net PnL', tot.net, true], ['Δ uPnL', tot.dUpnl, true], ['Net transfer', tot.transfer, true],
          ['Açıklanamayan fark', tot.residual, true]].map(([l, v, s]: any) => (
          <div key={l}>
            <p className={`text-[11px] ${t.muted}`}>{l}</p>
            <p className={`text-base font-bold font-mono ${s ? tone(v, t) : t.text}`}>{fmt(v, 2, s)}</p>
          </div>
        ))}
      </div>
      {!tot.complete && <p className="text-[11px] text-amber-400">Bazı hesapların equity'si okunamadı — equity toplamları yalnız iki uçta da okunan hesapları kapsar.</p>}

      {/* Hesaplar */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className={`${t.tableHeader} border-b`}>
            <th className={`px-2.5 py-2 text-left font-medium ${t.muted}`}>Hesap</th>
            {['Equity', 'Δ Equity', 'uPnL', 'Realized', 'Komisyon', 'Funding', 'Gaz', 'Net PnL', 'Transfer', 'Fark', 'İşlem'].map(h =>
              <th key={h} className={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rep.usd.map((r: any) => (
              <tr key={r.account} className={`border-b ${t.tableRow}`} title={(r.notes || []).join(' · ')}>
                <td className={`px-2.5 py-2 ${t.text} whitespace-nowrap`}>{r.name}{r.notes?.length ? <span className="text-amber-400"> *</span> : null}</td>
                <Num v={r.eq1} s={false} /><Num v={r.dEq} /><Num v={r.upnl1} /><Num v={r.pnl} /><Num v={-r.fee} />
                <Num v={r.funding} /><Num v={-r.gas} /><Num v={r.net} /><Num v={r.transfer} /><Num v={r.residual} />
                <td className={`${td} ${t.muted}`}>{r.trades}</td>
              </tr>
            ))}
            <tr className={`border-t-2 ${t.cardBorder} font-bold`}>
              <td className={`px-2.5 py-2 ${t.text}`}>Toplam</td>
              <Num v={tot.eq1} s={false} /><Num v={tot.dEq} /><Num v={tot.upnl1} /><Num v={tot.pnl} /><Num v={-tot.fee} />
              <Num v={tot.funding} /><Num v={-tot.gas} /><Num v={tot.net} /><Num v={tot.transfer} /><Num v={tot.residual} />
              <td className={`${td} ${t.muted}`}>{tot.trades}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {rep.usd.some((r: any) => r.notes?.length) && (
        <div className={`text-[11px] ${t.muted} space-y-0.5`}>
          {rep.usd.filter((r: any) => r.notes?.length).map((r: any) => <p key={r.account}>* {r.name}: {r.notes.join('; ')}</p>)}
        </div>
      )}
      {(rep.idle?.length > 0 || rep.unread?.length > 0) && (
        <p className={`text-[11px] ${t.muted}`}>
          {rep.idle?.length > 0 && <>Boş hesaplar: {rep.idle.join(', ')}. </>}
          {rep.unread?.length > 0 && <>Okunamayan: {rep.unread.join(', ')}.</>}
        </p>
      )}

      {/* COIN-M */}
      {rep.coinm?.length > 0 && (
        <div>
          <p className={`text-xs font-semibold mb-2 ${t.text}`}>Binance COIN-M <span className={`font-normal ${t.muted}`}>(coin cinsinden)</span></p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className={`${t.tableHeader} border-b`}>
                <th className={`px-2.5 py-2 text-left font-medium ${t.muted}`}>Coin</th>
                {['Equity', 'Δ Equity', 'Realized', 'Komisyon', 'Funding', 'Grid PnL', 'Net', 'Transfer', 'Fark'].map(h =>
                  <th key={h} className={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {rep.coinm.map((r: any) => (
                  <tr key={r.currency} className={`border-b ${t.tableRow}`}>
                    <td className={`px-2.5 py-2 font-semibold ${t.text}`}>{r.currency}</td>
                    <Num v={r.eq1} d={8} s={false} /><Num v={r.dEq} d={8} /><Num v={r.pnl} d={8} /><Num v={-r.fee} d={8} />
                    <Num v={r.funding} d={8} /><Num v={r.grid} d={8} /><Num v={r.net} d={8} /><Num v={r.transfer} d={8} /><Num v={r.residual} d={8} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Navi */}
      {rep.navi?.length > 0 && (
        <div>
          <p className={`text-xs font-semibold mb-2 ${t.text}`}>Navi <span className={`font-normal ${t.muted}`}>(coin bazında, supply − borrow)</span></p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className={`${t.tableHeader} border-b`}>
                <th className={`px-2.5 py-2 text-left font-medium ${t.muted}`}>Coin</th>
                {['Gün başı', 'Gün sonu', 'Değişim', 'Faiz', 'Yatırma/çekme', 'Gün sonu $'].map(h => <th key={h} className={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {rep.navi.map((a: any) => (
                  <tr key={a.wallet + a.coin} className={`border-b ${t.tableRow}`}>
                    <td className={`px-2.5 py-2 font-semibold ${t.text}`}>{a.coin}</td>
                    <Num v={a.start} d={6} s={false} /><Num v={a.end} d={6} s={false} /><Num v={a.delta} d={6} />
                    <Num v={a.interest} d={6} /><Num v={a.flow} d={6} />
                    <Num v={a.price ? a.end * a.price : null} s={false} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className={`text-[10px] ${t.muted}`}>
        Net PnL = Realized − Komisyon + Funding − Gaz. Fark = Δ Equity − (Net PnL + Δ uPnL + Transfer); sıfırdan sapma kaçan bir transfer ya da eksik senkron demektir.
      </p>
    </div>
  );
}
